import useAnalytics from '@/hooks/use-analytics';
import {
  Box,
  Stepper as MuiStepper,
  Step as MuiStep,
  StepLabel as MuiStepLabel,
  StepButton as MuiStepButton,
  StepConnector,
  stepConnectorClasses,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { NavigationStepper as NavigationStepperType } from '@/schemas/sceneDataSchema';
import { getRouteStep, setRoute } from '@/services/location-service';
import { useScopesValidity } from '@/hooks/use-entity-validity';

function StepItem({
  step,
  trackEvent,
  index,
  last,
}: {
  step: NavigationStepperType['steps'][number];
  trackEvent: ReturnType<typeof useAnalytics>['trackEvent'];
  index?: number;
  last?: boolean;
}) {
  const { step_id, errored, navigable, completed, label, entity_index, validation_scopes } = step;
  const currentStepId = getRouteStep().step_id;
  const isCurrentStep = step_id === currentStepId;
  const scopesValidity = useScopesValidity(validation_scopes ?? []);

  // When the store has data for this step's scopes, use client-side validity;
  // otherwise fall back to the server-computed value.
  const isErrored = scopesValidity !== null ? !scopesValidity : errored;

  const handleClick = () => {
    const pathName = setRoute({ step_id, entity_index });
    trackEvent('NAVIGATION_STEPPER_STEP_CLICKED', { step_id });
    if (pathName) {
      window.location.assign(pathName);
    }
  };

  return (
    <MuiStep
      key={step_id}
      index={index}
      last={last}
      completed={completed && !isCurrentStep}
    >
      {navigable ? (
        <MuiStepButton
          onClick={handleClick}
          data-dd-action-name={`Navigation Stepper Button - ${step_id}`}
        >
          <StyledStepLabel error={isErrored}>{label}</StyledStepLabel>
        </MuiStepButton>
      ) : (
        <StyledStepLabel
          error={isErrored}
          data-dd-action-name={`Navigation Stepper Label - ${step_id}`}
        >
          {label}
        </StyledStepLabel>
      )}
    </MuiStep>
  );
}

export const NavigationStepper = ({ component }: { component: NavigationStepperType }) => {
  const { steps } = component;
  const { trackEvent } = useAnalytics();

  const getActiveStep = (steps: NavigationStepperType['steps']): number => {
    const { step_id } = getRouteStep();
    const stepIndex = steps.findIndex((step) => step.step_id === step_id);
    return stepIndex !== -1 ? stepIndex : 0;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <MuiStepper
        activeStep={getActiveStep(steps)}
        connector={<StyledConnector />}
        nonLinear
      >
        {steps.map((step) => (
          <StepItem
            key={step.step_id}
            step={step}
            trackEvent={trackEvent}
          />
        ))}
      </MuiStepper>
    </Box>
  );
};

// Styled Components
const StyledStepLabel = styled(MuiStepLabel)(({ theme }) => ({
  '& .MuiStepIcon-root': {
    width: 24,
    height: 24,
    fontSize: 24,
    '&.Mui-completed': {
      color: theme.palette.text.secondary,
    },
    '&.Mui-error': {
      color: theme.palette.warning.main,
    },
  },
  '& .MuiStepLabel-iconContainer': {
    paddingRight: 8,
  },
  '& .MuiStepLabel-label': {
    ...theme.typography.h4,
    '&.Mui-active': {
      color: theme.palette.text.primary,
    },
    '&.Mui-completed': {
      color: theme.palette.text.secondary,
    },
    '&.Mui-disabled': {
      color: theme.palette.text.disabled,
    },
    '&.Mui-error': {
      color: theme.palette.warning.main,
    },
  },
}));

const fillLine = keyframes`
  from { width: 0%; }
  to { width: 100%; }
`;

const StyledConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 1,
    border: 0,
    backgroundColor: theme.palette.grey[400],
    position: 'relative',
    overflow: 'hidden',
  },
  // Target the line when the step is active OR completed
  [`&.${stepConnectorClasses.active}, &.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}::before`]: {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      backgroundColor: theme.palette.primary.main,
      animation: `${fillLine} 0.8s ease-in-out forwards`,
    },
  },
}));
